/**
 * Admin Kiosk Visitor Detail API Contract
 *
 * Defines the wire format for the kiosk visitor BFF endpoint:
 *   GET /api/v1/admin/kiosk-visitor-detail/:userId  (ticketing-service)
 *
 * Shared between ticketing-service (producer) and admin frontend (consumer).
 * Reuses AdminCustomerOrderSchema and AdminCustomerTicketSchema from customer-detail.ts.
 */

import { z } from 'zod';

import { AdminCustomerOrderSchema, AdminCustomerTicketSchema } from './customer-detail';

export const AdminKioskWristbandSchema = z.object({
  id: z.string(),
  wristbandId: z.string(),   // short 8-char ID printed on the band
  status: z.string(),        // active | suspended | lost | void
  issuedAt: z.string(),
  expiresAt: z.string(),
  activatedAt: z.string().nullable(),
  lastScanAt: z.string().nullable(),
  totalScanCount: z.number().int().nonnegative(),
  printerId: z.string().nullable(),
});
export type AdminKioskWristband = z.infer<typeof AdminKioskWristbandSchema>;

export const AdminKioskDeviceSchema = z.object({
  serialNumber: z.string(),
  location: z.string(),
  zoneId: z.string(),
  deviceType: z.string(),
  status: z.string(),
  capabilities: z.array(z.string()),
  lastHeartbeatAt: z.string().datetime().nullable(),
  activatedAt: z.string().datetime().nullable(),
});
export type AdminKioskDevice = z.infer<typeof AdminKioskDeviceSchema>;

export const AdminKioskVisitorDetailResponseSchema = z.object({
  kiosk: z.object({
    userId: z.string(),
    kioskId: z.string(),
    name: z.string(),
    status: z.string(),
    createdAt: z.string(),
  }),
  device: AdminKioskDeviceSchema.nullable(),
  orders: z.array(AdminCustomerOrderSchema),
  tickets: z.array(AdminCustomerTicketSchema),
  wristbands: z.array(AdminKioskWristbandSchema),
  summary: z.object({
    totalOrders: z.number().int().nonnegative(),
    totalRevenue: z.number().nonnegative(),
    averagePerOrder: z.number().nonnegative(),
    lastOrderDate: z.string().datetime().nullable(),
  }),
});
export type AdminKioskVisitorDetailResponse = z.infer<typeof AdminKioskVisitorDetailResponseSchema>;
